<template>
  <v-container pa-0 fluid>
    <v-row v-if="locked">
      <v-col cols="12">
        <bm-alert type="warning" class="mb-0">
          <slot name="warning-text">
            {{ $m.cannot_clear_signature }}
          </slot>
        </bm-alert>
      </v-col>
    </v-row>

    <v-row v-if="hasHeader">
      <v-col cols="12">
        <slot name="signature-header" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <bm-control v-bind="$attrs">
          <template #default="{ dataQa }">
            <v-container pa-0>
              <v-row no-gutters :class="{ 'sig-wrapper': !readOnly }">
                <v-col v-if="!readOnly" cols="auto" class="clear-signature">
                  <bm-btn
                    button-type="ghost"
                    :t="$m.keys.delivery_app_clear"
                    @click="clearSignature"
                  />
                </v-col>
                <v-col cols="12">
                  <!-- Wouldn't register the 'ref' using v-if, so I've had to use v-show -->
                  <vue-signature-pad
                    v-show="!locked && !readOnly"
                    :key="signaturePadKey"
                    ref="signaturePad"
                    height="200px"
                    :options="{ backgroundColor: '#F4F7FB', onEnd }"
                    :data-qa="dataQa"
                  />
                </v-col>
                <v-col v-if="locked || readOnly" cols="12">
                  <template>
                    <img
                      v-if="signatureSvg !== null"
                      :src="`data:image/svg+xml,${signatureSvg}`"
                      alt="Signature"
                      class="img-svg"
                    />
                    <bm-read-only-field v-else>
                      {{ displayString(null) }}
                    </bm-read-only-field>
                  </template>
                </v-col>
              </v-row>
            </v-container>
          </template>
        </bm-control>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import BmAlert from "@/components/controls/BmAlert.vue";
import BmControl from "@/components/controls/input/BmControl.vue";
import BmReadOnlyField from "@/components/controls/input/BmReadOnlyField.vue";
import type { PropType } from "vue";
import { computed, defineComponent, ref, watch } from "vue";
import { useDisplayHelpers } from "@/components/mixins/DisplayHelpers";

export default defineComponent({
  name: "BmSignature",
  components: {
    BmAlert,
    BmControl,
    BmReadOnlyField,
  },
  props: {
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    locked: {
      type: Boolean,
      required: false,
      default: false,
    },
    signatureSvg: {
      type: Object as PropType<string | null>,
      required: false,
      default: null,
    },
  },
  setup(props, context) {
    // $refs
    const signaturePad = ref<any>(null);
    const { displayString } = useDisplayHelpers();

    const clearSignature = () => {
      signaturePad.value.clearSignature();
      context.emit("clear-signature");
    };

    const onEnd = () => {
      let svgSignatureBase64DataUri =
        signaturePad.value.saveSignature("image/svg+xml")["data"];

      const svgXml = atob(svgSignatureBase64DataUri.split(",")[1] ?? "");

      context.emit("save-signature", svgXml);
    };

    const hasHeader = computed(() => {
      return !!context.slots["signature-header"];
    });

    // re-render the signature pad when toggling locked state
    const signaturePadKey = ref(0);
    watch(
      () => props.locked,
      () => {
        signaturePadKey.value++;
      }
    );

    return {
      onEnd,
      clearSignature,
      hasHeader,
      signaturePad,
      displayString,
      signaturePadKey,
    };
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/colours"

.sig-wrapper
  border: solid 1px #000
  max-width: 100%
  padding: 0

.clear-signature
  position: absolute

.img-svg
  max-width: 100%

// ensure a minimum size for the canvas
:deep(canvas)
  min-width: 100px
  min-height: 100px
</style>
